import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import api from 'state/axios';
import { formatDate } from 'state/helpers';
import CustomDatePicker from 'components/CustomDatePicker';
import SectionLabel from 'components/SectionLabel';
import FlexBetween from 'components/FlexBetween';


const BLANK_QUAL = {
  post: '',
  base: '',
  acType: '',
  primaryQual: false,
  rosterGroup: 0,
  notToBeRoster: false,
  begDay: null,
  endDay: null,
};


const Qualifications = ({ 
  values, 
  setFieldValue, 
  isEditMode,
}) => {
  const crewRanks = useSelector((state) => state.crewRanks);
  const [acTypes, setAcTypes] = useState([]);

  useEffect(() => {
    api.get('/actypes/active')
      .then((res) => setAcTypes(res.data || []))
      .catch(() => {});
  }, []);

  const langs = values.language || [];
  const addLang = () => setFieldValue('language', [...langs, '']);
  const removeLang = (i) => setFieldValue('language', langs.filter((_, idx) => idx !== i));
  const updateLang = (i, val) => {
    const updated = [...langs];
    updated[i] = val;
    setFieldValue('language', updated);
  };

  const quals = values.crewQuals || [];

  const addQual    = () => setFieldValue('crewQuals', [...quals, { ...BLANK_QUAL }]);
  const removeQual = (i) => setFieldValue('crewQuals', quals.filter((_, idx) => idx !== i));
  const updateQual = (i, field, val) =>
    setFieldValue('crewQuals', quals.map((q, idx) => idx === i ? { ...q, [field]: val } : q));

  if (!isEditMode) return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
      gap={2}
    >
      <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
        <SectionLabel>Languages</SectionLabel>   
        {langs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">-</Typography>
        ) : (
          langs?.map((l, i) => (
            <Typography key={i} variant="body2">{l || '-'}</Typography>
          ))
        )}
      </Box>
      
      <Box 
        display="flex" 
        flexDirection="column" 
        gap={1} 
        sx={{ gridColumn: 'span 12' }}
      >
        <SectionLabel>Crew Qualifications</SectionLabel>
        {quals.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No qualifications recorded.</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Post</TableCell>
                  <TableCell>Base</TableCell>
                  <TableCell>ACType</TableCell>
                  <TableCell>Primary</TableCell>
                  <TableCell>RosterGroup</TableCell>
                  <TableCell>NotToRoster</TableCell>
                  <TableCell>BegDay</TableCell>
                  <TableCell>EndDay</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quals.map((q, i) => (
                  <TableRow key={i}>
                    <TableCell>{q.post || '-'}</TableCell>
                    <TableCell>{q.base || '-'}</TableCell>
                    <TableCell>
                      {q.acType ? <Chip label={q.acType} size="small" color="primary" variant="outlined" /> : '-'}
                    </TableCell>
                    <TableCell>{q.primaryQual ? '✓' : ''}</TableCell>
                    <TableCell>{q.rosterGroup ?? '-'}</TableCell>
                    <TableCell>{q.notToBeRoster ? '✓' : ''}</TableCell>
                    <TableCell>{formatDate(q.begDay)}</TableCell>
                    <TableCell>{formatDate(q.endDay)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  );


  return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
      gap={2}
    >
      <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 12' } }}>
        <FlexBetween mb={1}>
          <SectionLabel>Languages</SectionLabel>   
          <Button size="small" startIcon={<Add />} onClick={addLang}>
            Add Language
          </Button>
        </FlexBetween>
        {langs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No languages recorded.
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={1}>
            {langs.map((l, i) => (
              <Box key={i} display="flex" alignItems="center" gap={1}>
                <TextField
                  fullWidth
                  size="small"
                  label={`Language ${i + 1}`}
                  value={l}
                  onChange={(e) => updateLang(i, e.target.value)}
                />
                <IconButton 
                  size="small" 
                  color="error" 
                  onClick={() => removeLang(i)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ gridColumn: 'span 12' }}>
      <FlexBetween mb={1}>
        <SectionLabel>Crew Qualifications</SectionLabel>   
        <Button size="small" startIcon={<Add />} onClick={addQual}>
          Add Qualification
        </Button>
      </FlexBetween>

      {quals.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No qualifications recorded.
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {quals.map((q, i) => (
            <Box
              key={i}
              display="flex"
              alignItems="center"
              gap={2}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
            >
              <Box 
                flex={1}
                display="grid" 
                gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
                gap={2} 
              >
                <TextField
                  fullWidth 
                  select 
                  size="small" 
                  label="Post"
                  value={q.post || ''}
                  onChange={(e) => updateQual(i, 'post', e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 2' } }}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {crewRanks?.map((r) => (
                    <MenuItem key={r._id} value={r.rankCode}>
                      {r.rankCode}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth 
                  select 
                  size="small" 
                  label="AC Type"
                  value={q.acType || ''}
                  onChange={(e) => updateQual(i, 'acType', e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 2' } }}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {acTypes.map((ac) => (
                    <MenuItem key={ac.acTypeCode} value={ac.acTypeCode}>
                      {ac.acTypeCode}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth 
                  size="small" 
                  label="Base"
                  value={q.base}
                  onChange={(e) => updateQual(i, 'base', e.target.value)}
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 2' } }}
                />

                <TextField
                  fullWidth 
                  size="small" 
                  label="Roster Group" 
                  type="number"
                  value={q.rosterGroup}
                  onChange={(e) => updateQual(i, 'rosterGroup', Number(e.target.value))}
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 2' } }}
                />

                <CustomDatePicker
                  variant="outlined"
                  size="small" 
                  label="Beg Day" 
                  name="begDay"
                  value={q.begDay ? new Date(q.begDay) : null}
                  onChange={(_, d) => updateQual(i, 'begDay', d)}
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 2' } }}
                />

                <CustomDatePicker
                  variant="outlined"
                  size="small" 
                  label="End Day" 
                  name="endDay"
                  value={q.endDay ? new Date(q.endDay) : null}
                  onChange={(_, d) => updateQual(i, 'endDay', d)}
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 2' } }}
                />

                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gridColumn: { xs: 'span 12', sm: 'span 2' }, 
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch 
                        size="small" 
                        checked={q.primaryQual}
                        onChange={(e) => updateQual(i, 'primaryQual', e.target.checked)}
                      />
                    }
                    label="Primary"
                  />
                </Box>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gridColumn: { xs: 'span 12', sm: 'span 2' }, 
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch 
                        size="small" 
                        checked={q.notToBeRoster}
                        onChange={(e) => updateQual(i, 'notToBeRoster', e.target.checked)}
                      />
                    }
                    label="Not to Roster"
                  />
                </Box>
              </Box>

              <Box display="flex" justifyContent="flex-end">
                <IconButton 
                  size="small" 
                  color="error" 
                  onClick={() => removeQual(i)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}
      </Box>
    </Box>
  );
};

export default Qualifications;
